import { ImageSizes, ImageInterface } from '../../types';

const splitObject = (imageSizes: ImageSizes, splitKey: number) => {
  const mobileImageSizes: ImageSizes = {};
  const desktopImageSizes: ImageSizes = {};

  for (const [key, value] of Object.entries(imageSizes)) {
    (Number(key) < splitKey ? mobileImageSizes : desktopImageSizes)[key] =
      value;
  }

  return { mobileImageSizes, desktopImageSizes };
};

const addSizes = (imageSizes: ImageSizes) => {
  const sortedKeys = Object.keys(imageSizes)
    .map(Number)
    .filter((key) => key > 0)
    .sort((a, b) => b - a);

  return [
    ...sortedKeys.map((key) => `(min-width: ${key}px) ${imageSizes[key][0]}px`),
    imageSizes[0] ? `${imageSizes[0][0]}px` : '100vw',
  ].join(', ');
};

const addSources = (
  baseSources: string,
  imageSizes: ImageSizes,
  fileDimensions: { width: number; height: number },
) => {
  const supportedDprValues = [1, 1.25, 1.5, 2, 3];
  const baseUrl = baseSources.split(' ')[0];
  const url = new URL(baseUrl.startsWith('//') ? `https:${baseUrl}` : baseUrl);
  const sizes = Object.values(
    Object.values(imageSizes)
      .flatMap((size) =>
        supportedDprValues.map((dpr) =>
          size.map((item) => Math.ceil(item * dpr)),
        ),
      )
      .reduce((acc, [width, height]) => {
        acc[width] = acc[width] ?? [width, height];
        return acc;
      }, {} as Record<number, number[]>),
  );

  return sizes
    .map(([width, height]) => {
      const boundedWidth = Math.min(fileDimensions.width, width);
      const boundedHeight = Math.min(fileDimensions.height, height);

      url.searchParams.set('w', boundedWidth.toString());
      url.searchParams.set('h', boundedHeight.toString());

      return `${url} ${boundedWidth}w`;
    })
    .sort((a, b) => parseInt(a.split(' ')[1]) - parseInt(b.split(' ')[1]))
    .join(',\n');
};

const addFocusPoint = ({
  sources,
  focusPoint,
  imageFill,
}: {
  sources: string;
  focusPoint?: ImageInterface['focusPoint'];
  imageFill?: ImageInterface['imageFill'];
}) =>
  sources
    .split('\n')
    .map((item) => {
      const [baseUrl, descriptor] = item.split(' ');
      const url = new URL(
        baseUrl.startsWith('//') ? `https:${baseUrl}` : baseUrl,
      );

      if (imageFill) {
        url.searchParams.set('fit', 'fill');
      } else if (url.searchParams.get('fit') === 'fill') {
        url.searchParams.delete('fit');
      }

      if (focusPoint) url.searchParams.set('f', focusPoint);

      return descriptor ? `${url} ${descriptor}` : url.toString();
    })
    .join('\n');

export const setImageProperties = ({
  imageData,
  imageDataMobile,
  focusPoint,
  focusPointMobile,
  imageSizes,
  imageFill,
}: {
  imageData: ImageInterface['image'];
  imageDataMobile?: ImageInterface['mobileImage'];
  focusPoint?: ImageInterface['focusPoint'];
  focusPointMobile?: ImageInterface['mobileFocusPoint'];
  imageSizes?: ImageInterface['imageSizes'];
  imageFill?: ImageInterface['imageFill'];
}) => {
  const data = imageData.gatsbyImageData;
  const mobileData = imageDataMobile?.gatsbyImageData;
  const mobileBreakpoint = 1023;
  const mediaQueryMax = `(max-width: ${mobileBreakpoint}px)`;
  const mobileImageRatio = mobileData
    ? mobileData.width / mobileData.height
    : undefined;

  /*
		Step 1: Merge mobile and desktop sources if mobile data exists
	*/
  if (data?.images?.sources?.length && mobileData?.images?.sources?.length) {
    data.images.sources = [
      ...mobileData.images.sources.map((item) => ({
        ...item,
        media: mediaQueryMax,
      })),
      ...data.images.sources,
    ];
  }

  /*
		Step 2: Add sizes, sources and focus points.
		The image sources are located under:
		- images['fallback']['srcSet']
		- images['sources'][n]['srcSet']
	*/
  if (data?.images?.fallback) {
    data.images.fallback = {
      ...data.images.fallback,
      sizes: imageSizes ? addSizes(imageSizes) : data.images.fallback.sizes,
    };
  }

  if (data?.images?.sources?.length) {
    if (imageSizes && imageData.file?.details?.image) {
      const fileDimensions = imageData.file.details.image;
      const { mobileImageSizes } = splitObject(imageSizes, mobileBreakpoint);

      data.images.sources = data.images.sources.map((item) => ({
        ...item,
        srcSet: addFocusPoint({
          sources: addSources(
            item.srcSet,
            item.media === mediaQueryMax ? mobileImageSizes : imageSizes,
            fileDimensions,
          ),
          focusPoint:
            item.media === mediaQueryMax ? focusPointMobile : focusPoint,
          imageFill,
        }),
      }));
    } else {
      data.images.sources = data.images.sources.map((item) => ({
        ...item,
        srcSet: addFocusPoint({
          sources: item.srcSet,
          focusPoint:
            item.media === mediaQueryMax ? focusPointMobile : focusPoint,
          imageFill,
        }),
      }));
    }
  }

  return { data, mobileMediaQuery: mediaQueryMax, mobileImageRatio };
};

export const getImagePosition = (focusPoint?: ImageInterface['focusPoint']) =>
  ({
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    right: 'right center',
    left: 'left center',
    top_right: 'right top',
    top_left: 'left top',
    bottom_right: 'right bottom',
    bottom_left: 'left bottom',
    face: 'center center',
    faces: 'center center',
  }[focusPoint || 'center']);
